<script setup lang="ts">
import BaseOverlay from '@/overlays/BaseOverlay.vue';
import { computed, ref } from 'vue';
import Status from '@/components/shared/infoLabels/Status.vue';
import EditWorkScheduleControls from '@/components/shared/EditWorkScheduleControls.vue';
import TimeBlock from '@/components/shared/blocks/TimeBlock.vue';
import { isAfter } from 'date-fns';
import constants from '@/utils/constants';
import { useRoute } from 'vue-router';
import i18n from '@/utils/i18n';
import { toDate } from 'date-fns-tz';
import { getSimpleTime } from '@/composables/getSimpleTime.js';
import { useInstitutionStore } from '@/stores/institution';
import { useDepartmentStore } from '@/stores/department';
import { useWorkScheduleStore } from '@/stores/workSchedule';
import { UpdateShiftAction } from '@/interfaces';
import { ShiftFragment } from '@/graphql/custom-types';
import BasicBlock from '@/components/shared/blocks/BasicBlock.vue';
import UpdateShiftFlow from '@/components/workSchedule/UpdateShiftFlow.vue';
import { formatTime } from '@/utils/helpers';
import BlocksWrapper from '@/components/shared/blocks/BlocksWrapper.vue';

const emit = defineEmits(['close']);

interface Props {
  title?: string;
  label?: string;
  shift: ShiftFragment;
}

withDefaults(defineProps<Props>(), {});

const institutionStore = useInstitutionStore();
const departmentStore = useDepartmentStore();
const workScheduleStore = useWorkScheduleStore();
const route = useRoute();

const pinOverlayTitle = ref<string>();
const successOverlayTitle = ref<string>('');
const showUpdateShiftFlow = ref<boolean>(false);
const updateAction = ref<UpdateShiftAction>('cancel');

const isNormalWS = computed(() => {
  return route.name === 'WORK_SCHEDULE';
});

const shiftEnd = computed(() => {
  return toDate(shift.value.end, { timeZone: constants.timezone });
});

const unavailabilityStart = computed(() => {
  const item = shift.value.unavailable?.[0];
  if (item?.start) {
    return toDate(item.start, { timeZone: constants.timezone });
  }
  return undefined;
});

const unavailabilityEnd = computed(() => {
  const item = shift.value.unavailable?.[0];
  if (item?.end) {
    return toDate(item.end, { timeZone: constants.timezone });
  }
  return undefined;
});

const shift = computed((): ShiftFragment => {
  return workScheduleStore.selectedShift as ShiftFragment;
});

const unavailableDuration = computed(() => {
  const item = shift.value.unavailable?.[0];
  if (item?.start && item?.end) {
    return `${formatTime(item.start)} - ${formatTime(item.end)}`;
  }
  return undefined;
});

const unavailableNow = computed(() => {
  if (
    !shift.value.unavailable ||
    !unavailabilityStart.value ||
    !unavailabilityEnd.value
  ) {
    return false;
  }

  if (
    isAfter(new Date(), unavailabilityStart.value) &&
    isAfter(unavailabilityEnd.value, new Date())
  ) {
    return true;
  }

  return false;
});

const unavailabilityDescription = computed(() => {
  return shift.value.unavailable?.[0]?.description ?? undefined;
});

const shiftOver = computed(() => {
  return isAfter(new Date(), shiftEnd.value);
});

const canEditWorkSchedule = computed(() => {
  return !institutionStore.settings?.useExternalWorkSchedule;
});

const simpleTime = computed(() => {
  return getSimpleTime(departmentStore.settings);
});

function getProfileImage() {
  const employee = shift.value.employee;

  if (!employee?.showImage || !employee?.displayImage?.length)
    return constants.avatarPlaceholder;

  return institutionStore.settings?.general?.vismaGatWs
    ? `data:image/jpg;base64,${employee.displayImage}`
    : employee.displayImage;
}

const iconCircleProps = computed(() => {
  if (shift.value.nightShift) {
    return {
      icon: ['fas', 'moon-stars'],
      iconColor: 'text-neutral-0',
      backgroundColor: 'bg-charcoal',
    };
  }

  if (shift.value.cancelled) {
    return {
      icon: ['far', 'times'],
      iconColor: 'text-white',
      backgroundColor: 'bg-warning',
    };
  }

  if (unavailableNow.value) {
    return { backgroundColor: 'bg-orange' };
  }

  return {
    icon: undefined,
    iconColor: '',
    backgroundColor: 'bg-darkOcean-600',
  };
});

const cancelShift = () => {
  showUpdateShiftFlow.value = true;
  updateAction.value = 'cancel';

  pinOverlayTitle.value = i18n.global.t('workSchedule.cancel.title', {
    name: shift.value.employee.displayName,
  });

  successOverlayTitle.value = i18n.global.t(
    'workSchedule.cancel.confirmation',
    {
      name: shift.value.employee.displayName,
    }
  );
};

const reactivateShift = () => {
  showUpdateShiftFlow.value = true;
  updateAction.value = 'reactivate';

  pinOverlayTitle.value = i18n.global.t('workSchedule.reactivate.title', {
    name: shift.value.employee.displayName,
  });

  successOverlayTitle.value = i18n.global.t(
    'workSchedule.reactivate.confirmation',
    {
      name: shift.value.employee.displayName,
    }
  );
};

const replaceShift = () => {
  showUpdateShiftFlow.value = true;
  updateAction.value = 'replace';

  pinOverlayTitle.value = i18n.global.t('workSchedule.replace.title', {
    name: shift.value.employee.displayName,
  });

  successOverlayTitle.value = i18n.global.t(
    'workSchedule.replace.confirmation',
    {
      name: shift.value.employee.displayName,
    }
  );
};

const deleteShift = () => {
  showUpdateShiftFlow.value = true;
  updateAction.value = 'delete';

  pinOverlayTitle.value = i18n.global.t('workSchedule.delete.title', {
    name: shift.value.employee.displayName,
  });

  successOverlayTitle.value = i18n.global.t(
    'workSchedule.delete.confirmation',
    {
      name: shift.value.employee.displayName,
    }
  );
};

type updateActions = 'cancel' | 'reactivate' | 'replace' | 'delete';

const updateShift = (action: updateActions) => {
  if (action === 'cancel') cancelShift();
  if (action === 'reactivate') reactivateShift();
  if (action === 'replace') replaceShift();
  if (action === 'delete') deleteShift();
};
</script>

<template>
  <BaseOverlay
    title-position="center"
    :title="title"
    :label="label"
    :header-image="getProfileImage()"
    :icon-circle="iconCircleProps"
    :grayscale-image="shiftOver || shift.cancelled || undefined"
    @close="emit('close')"
  >
    <template #content>
      <BlocksWrapper>
        <div class="flex items-center justify-center">
          <div
            v-if="shift.cancelled"
            class="bg-warning rounded-lg py-4 px-6 flex items-center justify-center w-max"
          >
            <p class="labelSmall">{{ $t('scheduleShift.notComing') }}</p>
          </div>
        </div>

        <BasicBlock
          v-if="shift.nightShift"
          :text="$t('global.nightShift')"
          :icon="['fal', 'moon-stars']"
        />

        <BasicBlock
          :text="
            shift.cancelled
              ? $t('scheduleShift.notComing')
              : $t('employee.working')
          "
          :icon="['fal', 'calendar']"
        />

        <TimeBlock v-if="isNormalWS" :start="shift.start" :end="shift.end" />

        <BasicBlock
          v-else
          :icon="['far', simpleTime.icon]"
          :text="simpleTime.name"
        />

        <BasicBlock v-if="shift.unavailable" :icon="['fal', 'fa-ban']">
          <div class="flex justify-between w-full">
            <div class="flex flex-col">
              <p class="h700">{{ $t('global.busy') }}</p>
              <p class="b500">
                <span>{{ unavailableDuration }}</span>
                <span v-if="unavailabilityDescription">
                  - {{ unavailabilityDescription }}</span
                >
              </p>
            </div>

            <Status
              v-if="unavailableNow"
              color="orange"
              icon="clock"
              :shadow="false"
            />
          </div>
        </BasicBlock>
      </BlocksWrapper>
    </template>

    <template #actions>
      <EditWorkScheduleControls
        v-if="canEditWorkSchedule"
        :shift="shift"
        @update="updateShift"
      />
    </template>
  </BaseOverlay>

  <UpdateShiftFlow
    v-if="showUpdateShiftFlow && pinOverlayTitle"
    :title="pinOverlayTitle"
    :title-success="successOverlayTitle"
    :shift="shift"
    :action="updateAction"
    @close="showUpdateShiftFlow = false"
  />
</template>
